import React from 'react';
import styled from 'styled-components';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { AddProjectFormDataTypes } from './addProjectFormUtils';
import Button from '^/components/atoms/Button';

import LoadingIcon from '^/components/atoms/LoadingIcon';
import { useL10n } from '^/hooks';
import Text from './text';
import AddCollaborator from '../AddCollaborator/AddCollaborator';
import { HorizontalDivider } from '^/components/atoms/ContentsListItem';

const Root = styled.div({
  gap: '16px',
  display: 'flex',
  flexDirection: 'column',
});

const ButtonWrapper = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
});

interface Collaborator {
  role: string;
  email: string;
  isValid: boolean;
}

interface ProjectShareFormProps {
  setValue: UseFormSetValue<AddProjectFormDataTypes>;
  isValid: boolean;
  watch: UseFormWatch<AddProjectFormDataTypes>;
  isLoading: boolean;
}

const validateEmail = (email: string): boolean => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email?.trim());
};

const ProjectShareForm: React.FC<ProjectShareFormProps> = ({
  setValue,
  isValid,
  watch,
  isLoading,
}) => {
  const [l10n] = useL10n();
  const formCollaborators = watch('collaborators') ?? [];
  const [collaborators, setCollaborators] = React.useState<Collaborator[]>(
    formCollaborators.map((collaborator: { role: string; email: string; isValid: boolean }) => ({
      role: collaborator.role,
      email: collaborator.email,
      isValid: validateEmail(collaborator.email),
    }))
  );

  const isCollaboratorsSkipped = watch('isCollaboratorsSkipped');

  const handleCollaboratorChange = (values: Collaborator[]) => {
    setCollaborators(values);
    setValue('collaborators', values);
  };

  return (
    <Root>
      <AddCollaborator
        isValid={isValid}
        isLoading={isLoading}
        values={collaborators}
        onChange={handleCollaboratorChange}
      />
      <HorizontalDivider customStyles={{ marginTop: '40px' }} />
      <ButtonWrapper>
        <Button
          variant="secondary"
          size="large"
          data-testid="skip-button-and-create"
          onClick={() => {
            setValue('isCollaboratorsSkipped', true);
            setValue('collaborators', []);
          }}
        >
          {isLoading && isCollaboratorsSkipped ? (
            <>
              <LoadingIcon /> {l10n(Text.skipForNow)}
            </>
          ) : (
            l10n(Text.skipForNow)
          )}
        </Button>
        <Button
          variant="primary"
          size="large"
          data-testid="project-submit-button"
          customStyles={{ gap: '8px' }}
          disabled={!isValid || isLoading}
          onClick={() => {
            setValue('isCollaboratorsSkipped', false);
            setValue('collaborators', collaborators);
          }}
        >
          {isLoading && !isCollaboratorsSkipped ? (
            <>
              <LoadingIcon />
              {l10n(Text.createProject)}
            </>
          ) : (
            <>{l10n(Text.createProject)}</>
          )}
        </Button>
      </ButtonWrapper>
    </Root>
  );
};

export default ProjectShareForm;
